import { useState, useEffect, useCallback, ChangeEvent } from 'react';
import RateService from '../../AppSettings/services/RateService';
import EmployeeService from '../../AppSettings/services/EmployeeService';
import toastService from '../../AppSettings/services/ToastService';
import { useError } from '../../Context/ErrorContext';
import type { Rate, Employees, ErrorContextValue } from '../../types/team';

interface EmployeeRatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  employee: Employees | null;
  onRatesUpdated: () => void;
}

const EmployeeRatesModal = ({
  isOpen,
  onClose,
  employee,
  onRatesUpdated,
}: EmployeeRatesModalProps): JSX.Element | null => {
  const [rates, setRates] = useState<Rate[]>([]);
  const [ratesLoading, setRatesLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [billedRateId, setBilledRateId] = useState<string>('');
  const [costofworkRateId, setCostofworkRateId] = useState<string>('');
  const [fieldRateId, setFieldRateId] = useState<string>('');

  const { setError } = useError() as unknown as ErrorContextValue;

  const fetchRates = useCallback(async (): Promise<void> => {
    setRatesLoading(true);
    try {
      const data = await RateService.getRates();
      setRates(data);
    } catch (error) {
      console.error('Error fetching rates:', error);
    } finally {
      setRatesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchRates();
    }
  }, [isOpen, fetchRates]);

  useEffect(() => {
    if (employee && isOpen) {
      setBilledRateId(employee.billedrate ? String(employee.billedrate) : '');
      setCostofworkRateId(employee.costofworkrate ? String(employee.costofworkrate) : '');
      setFieldRateId(employee.feildrate ? String(employee.feildrate) : '');
    }
  }, [employee, isOpen]);

  const handleSave = async (): Promise<void> => {
    if (!employee) return;

    setSaving(true);
    try {
      await EmployeeService.updateEmployeeRates(employee.EmployeeID, {
        billedrate: billedRateId || null,
        costofworkrate: costofworkRateId || null,
        feildrate: fieldRateId || null,
      });
      toastService.success('Rates updated successfully', {
        description: `Rates for ${employee.FirstName} ${employee.LastName} have been saved.`,
      });
      onRatesUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating employee rates:', error);
      toastService.error('Failed to update rates', {
        description: 'An error occurred while saving the rates. Please try again.',
      });
      setError(error);
    } finally {
      setSaving(false);
    }
  };

  const getRateDisplay = (rateId: string): string => {
    const rate = rates.find((r) => r.rate_id === rateId);
    return rate ? `${rate.rate_title} - $${rate.rate}` : '';
  };

  if (!isOpen || !employee) {
    return null;
  }

  return (
    <div className="team-rates-modal-overlay">
      <div className="team-rates-modal">
        <div className="team-rates-modal-header">
          <h3>Assign Rates to {employee.FirstName} {employee.LastName}</h3>
          <button className="team-rates-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="team-rates-modal-body">
          {ratesLoading ? (
            <div className="team-rates-loading">Loading rates...</div>
          ) : rates.length === 0 ? (
            <div className="team-rates-empty">
              No rates configured yet. Please add company rates first.
            </div>
          ) : (
            <div className="employee-rates-form">
              <div className="employee-rates-field">
                <label htmlFor="billed-rate">Billed Rate</label>
                <select
                  id="billed-rate"
                  value={billedRateId}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setBilledRateId(e.target.value)
                  }
                  className="employee-rates-select"
                >
                  <option value="">-- Select Billed Rate --</option>
                  {rates.map((rate) => (
                    <option key={rate.rate_id} value={rate.rate_id}>
                      {rate.rate_title} - ${rate.rate}/hr
                    </option>
                  ))}
                </select>
                {billedRateId && (
                  <span className="employee-rates-current">
                    Current: {getRateDisplay(billedRateId)}
                  </span>
                )}
              </div>

              <div className="employee-rates-field">
                <label htmlFor="costofwork-rate">Cost of Work Rate</label>
                <select
                  id="costofwork-rate"
                  value={costofworkRateId}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setCostofworkRateId(e.target.value)
                  }
                  className="employee-rates-select"
                >
                  <option value="">-- Select Cost of Work Rate --</option>
                  {rates.map((rate) => (
                    <option key={rate.rate_id} value={rate.rate_id}>
                      {rate.rate_title} - ${rate.rate}/hr
                    </option>
                  ))}
                </select>
                {costofworkRateId && (
                  <span className="employee-rates-current">
                    Current: {getRateDisplay(costofworkRateId)}
                  </span>
                )}
              </div>

              <div className="employee-rates-field">
                <label htmlFor="field-rate">Field Rate</label>
                <select
                  id="field-rate"
                  value={fieldRateId}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setFieldRateId(e.target.value)
                  }
                  className="employee-rates-select"
                >
                  <option value="">-- Select Field Rate --</option>
                  {rates.map((rate) => (
                    <option key={rate.rate_id} value={rate.rate_id}>
                      {rate.rate_title} - ${rate.rate}/hr
                    </option>
                  ))}
                </select>
                {fieldRateId && (
                  <span className="employee-rates-current">
                    Current: {getRateDisplay(fieldRateId)}
                  </span>
                )}
              </div>

              <div className="employee-rates-actions">
                <button
                  className="team-rates-cancel-button"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="team-rates-save-button"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Rates'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeRatesModal;
